import { useMemo } from 'react';

let cachedWebGLSupportTier = null;

function releaseProbeContext(gl) {
  if (!gl || typeof gl.getExtension !== 'function') return;
  // The capability probe creates a real WebGL context; release it so it cannot
  // count against the browser's small active-context quota.
  const loseContext = gl.getExtension('WEBGL_lose_context');
  if (loseContext && typeof loseContext.loseContext === 'function') {
    loseContext.loseContext();
  }
}

export function getWebGLSupportTier() {
  if (cachedWebGLSupportTier) {
    return cachedWebGLSupportTier;
  }

  if (typeof document === 'undefined') {
    return 'full';
  }

  let gl = null;
  try {
    const canvas = document.createElement('canvas');
    canvas.width = 1;
    canvas.height = 1;
    gl =
      canvas.getContext('webgl2') ||
      canvas.getContext('webgl') ||
      canvas.getContext('experimental-webgl');

    if (!gl) {
      cachedWebGLSupportTier = 'none';
      return cachedWebGLSupportTier;
    }

    const rendererInfo = gl.getExtension('WEBGL_debug_renderer_info');
    const renderer = rendererInfo
      ? gl.getParameter(rendererInfo.UNMASKED_RENDERER_WEBGL)
      : '';

    const maxTextureSize = gl.getParameter(gl.MAX_TEXTURE_SIZE) || 0;
    const rendererText = String(renderer || '').toLowerCase();
    const isSoftwareRenderer =
      rendererText.includes('swiftshader') ||
      rendererText.includes('llvmpipe') ||
      rendererText.includes('software');

    if (isSoftwareRenderer || maxTextureSize < 4096) {
      cachedWebGLSupportTier = 'readonly';
      return cachedWebGLSupportTier;
    }

    cachedWebGLSupportTier = 'full';
    return cachedWebGLSupportTier;
  } catch {
    cachedWebGLSupportTier = 'none';
    return cachedWebGLSupportTier;
  } finally {
    releaseProbeContext(gl);
  }
}

export default function useWebGLSupport() {
  return useMemo(() => getWebGLSupportTier(), []);
}
